/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import { Button, Icon, Input, InputLabel, Select } from '@scuf/common';
import { useTranslation } from '@scuf/localization';
import React, { useEffect, useRef, useState } from 'react';
import descriptors from '../../../locales/en-US/descriptors';
import { MAGIC_VALUE } from '../../shared/Utils/Constants';
import { IConnectTabProps, UtilityTypeNumber } from '../IUtilityService';
import { ToastNotification } from '../Notification';
import './ConnectTab.scss';
import { SpecialCharacter } from '../../shared/Utils/Utils';
import {
  EMaaSNotificationCheck,
  setPassword,
  setUsername,
  validate
} from './ConnectTabUtils';

function ConnectTab(props: IConnectTabProps) {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const emptyAccountDetails = { accountNumber: '', utility: '' };
  const {
    allConnectDetails,
    setAllConnectDetails,
    validateMandatoryFields,
    changeUtility,
    utilityType,
    disableButton
  } = props;
  let { isSubmitted } = props;
  let { accountDetails } = allConnectDetails;
  const [isDisabled, setDisabled] = useState(false);
  const [getUtilityType, setUtilityType] = useState<UtilityTypeNumber>(
    utilityType
  );
  const [getUtilityName, setUtilityName] = useState('');
  const utilityNameRef = useRef('');
  const utilityOptions = [
    {
      text: 'Electricity',
      value: JSON.stringify({
        name: 'Electricity',
        id: '2FFEE473-07FB-436E-AFCC-0B13464B1B7E'
      })
    },
    {
      text: 'Water',
      value: JSON.stringify({
        name: 'Water',
        id: 'B59557AB-7DE3-4013-AFFE-8EAE68F2099B'
      })
    },
    {
      text: 'Gas',
      value: JSON.stringify({
        name: 'Gas',
        id: '344F9FD4-B18B-4B4A-A654-D9D9015A1CC3'
      })
    },
    {
      text: 'Sewer',
      value: JSON.stringify({
        name: 'Sewer',
        id: 'DE8FFAD6-E4BD-4809-8C96-9B980FAA62EF'
      })
    }
  ];
  useEffect(() => {
    accountDetails = props.allConnectDetails.accountDetails;
    isSubmitted = props.isSubmitted;
  }, [props]);
  useEffect(() => {
    validate(isSubmitted, validateMandatoryFields);
  }, [allConnectDetails.username, allConnectDetails.password]);
  //number of accounts allowed to enroll at a time
  const MaxAccountLimit = 10;
  const addAccount = () => {
    if (accountDetails.length < MaxAccountLimit) {
      let tempAccountDetails = accountDetails;
      tempAccountDetails.push(emptyAccountDetails);
      setAllConnectDetails({
        ...allConnectDetails,
        accountDetails: tempAccountDetails
      });
    }
  };
  const removeAccount = (key: number, getUtility: string) => {
    let tempAccountDetails = [...accountDetails];
    tempAccountDetails.splice(key, 1);
    setAllConnectDetails({
      ...allConnectDetails,
      accountDetails: tempAccountDetails
    });
    const disable: boolean = false;
    disableButton(disable);

    if (getUtility !== '') {
      setUtilityType({
        ...getUtilityType,
        [getUtility]:
          getUtilityType[getUtility as keyof typeof getUtilityType] - 1
      });
    }
  };
  const changeAccountNumber = (index: number, value: string) => {
    if (value.length <= MAGIC_VALUE.HUNDRED && !SpecialCharacter(value)) {
      let tempAccountDetails = [...accountDetails];
      tempAccountDetails[index].accountNumber = value;
      setAllConnectDetails({
        ...allConnectDetails,
        accountDetails: tempAccountDetails
      });
      validate(isSubmitted, validateMandatoryFields);
    }
  };

  useEffect(() => {
    const count =
      getUtilityType !== undefined
        ? getUtilityType[getUtilityName as keyof typeof getUtilityType]
        : 0;
    const accountLimit = parseInt(
      window?.ENV?.EMAAS_ACCOUNT_LIMIT,
      MAGIC_VALUE.TEN
    );

    if (count !== undefined && count > accountLimit) {
      const disable: boolean = true;
      disableButton(disable);
      setDisabled(true);
    } else {
      const disable: boolean = false;
      disableButton(disable);
      setDisabled(false);
    }
  }, [getUtilityType, getUtilityName]);

  let changeUtilityType = (index: number, value: string) => {
    let tempAccountDetails = [...accountDetails];
    tempAccountDetails[index].utility = value;
    setAllConnectDetails({
      ...allConnectDetails,
      accountDetails: tempAccountDetails
    });
    changeUtility(value);
    const UtilityArray = value.split('"');
    let utilityValueCheck = UtilityArray[MAGIC_VALUE.THREE];
    setUtilityName(utilityValueCheck);
    if (
      utilityNameRef.current === '' ||
      utilityNameRef.current === utilityValueCheck
    ) {
      utilityNameRef.current = utilityValueCheck;
      setUtilityType({
        ...getUtilityType,
        [utilityValueCheck]:
          getUtilityType[utilityValueCheck as keyof typeof getUtilityType] + 1
      });
    } else if (utilityNameRef.current !== utilityValueCheck) {
      setUtilityType({
        ...getUtilityType,
        [utilityNameRef.current]:
          getUtilityType[
            utilityNameRef.current as keyof typeof getUtilityType
          ] - 1,
        [utilityValueCheck]:
          getUtilityType[utilityValueCheck as keyof typeof getUtilityType] + 1
      });
      utilityNameRef.current = utilityValueCheck;
    } else {
      // TODO document why this block is empty
    }

    validate(isSubmitted, validateMandatoryFields);
  };

  return (
    <div className="connect-tab">
      <div className={EMaaSNotificationCheck(isDisabled)}>
        <ToastNotification
          title={t(d.TITLENOTIFICATION)}
          description={t(d.DESCRIPTIONNOTIFICATION)}
        />
      </div>
      <p className="service-provider-note">{t(d.PLEASEUSESERVICE)}</p>
      <p className="signin">{t(d.SIGNIN)}</p>
      <div className="signin-username">
        <Input
          placeholder="ex. john@example.com"
          onChange={(data) => {
            setUsername(data, setAllConnectDetails, allConnectDetails);
          }}
          label={t(d.USERNAME)}
          indicator="required"
          error={allConnectDetails.usernameError}
          value={allConnectDetails.username}
        />
      </div>
      <div className="signin-password">
        <Input
          placeholder="xxxxxxxxxxx"
          label={t(d.PASSWORD)}
          type="password"
          disablePasswordToggle={true}
          onChange={(password) =>
            setPassword(password, setAllConnectDetails, allConnectDetails)
          }
          indicator="required"
          error={allConnectDetails.passwordError}
          value={allConnectDetails.password}
        />
      </div>
      {accountDetails.map((account, key) => {
        return (
          <React.Fragment key={`account-${key}`}>
            <div className="account-details">
              <Input
                className="account-number"
                type="text"
                placeholder=""
                value={account.accountNumber?.toString()}
                onChange={(data) => {
                  changeAccountNumber(key, data);
                }}
                label={t(d.ACCNUMBER)}
                indicator="required"
                error={account.accountNumberError}
              />
              <div className={'utility'}>
                {/* <label className="utility-label">{t(d.Utillity)}</label> */}
                <InputLabel
                  className="utility-label"
                  label={t(d.UtilityType)}
                  indicator="required"
                />
                <Select
                  placeholder={t(d.SELECTUTILITY)}
                  options={utilityOptions}
                  value={account.utility}
                  onChange={(value) => {
                    changeUtilityType(key, value);
                  }}
                  error={account.utilityError}
                />
                {/* <span className="utility-error-mssg">{account.utilityError}</span> */}
              </div>
              {key > 0 && (
                <div className="remove">
                  <Button
                    onClick={() => removeAccount(key, getUtilityName)}
                    type="link"
                    icon={<Icon name="close" root="common" />}
                    content=""
                  />
                </div>
              )}
            </div>
          </React.Fragment>
        );
      })}
      <Button
        className="add-account"
        type="link"
        icon={<Icon name="badge-plus" root="common" />}
        onClick={() => {
          addAccount();
        }}
        disabled={isDisabled}
      >
        {t(d.ADDACCOUNTNUMBER)}
      </Button>
    </div>
  );
}
export default ConnectTab;
